import { supabase } from '../lib/supabase';
import { sendSMS, formatLoadSMS, formatPhoneNumber, validatePhoneNumber } from '../lib/gohighlevel';
import { createThread, sendThreadedSMS } from './smsThreading.service';

interface SendDispatchSMSParams {
  dispatchId: string;
  loadId: string;
  driverId: string;
  truckId: string;
}

/**
 * Send SMS notification when load is dispatched to driver
 */
export async function sendDispatchSMS(params: SendDispatchSMSParams) {
  const { dispatchId, loadId, driverId, truckId } = params;

  try {
    // 1. Get driver information
    const { data: driver, error: driverError } = await supabase
      .from('drivers')
      .select('first_name, last_name, phone_number')
      .eq('id', driverId)
      .single();

    if (driverError || !driver) {
      throw new Error('Driver not found');
    }

    if (!driver.phone_number) {
      throw new Error('Driver phone number not available');
    }

    // Validate phone number
    if (!validatePhoneNumber(driver.phone_number)) {
      throw new Error('Invalid driver phone number format');
    }

    // 2. Get load details
    const { data: load, error: loadError } = await supabase
      .from('loads')
      .select(`
        load_number,
        commodity,
        weight,
        pickup_date,
        delivery_date,
        pickup_city,
        pickup_state,
        delivery_city,
        delivery_state
      `)
      .eq('id', loadId)
      .single();

    if (loadError || !load) {
      throw new Error('Load not found');
    }

    // 3. Get pickup and delivery stops
    const { data: stops, error: stopsError } = await supabase
      .from('load_stops')
      .select('*')
      .eq('load_id', loadId)
      .order('stop_order');

    if (stopsError) {
      throw new Error('Failed to fetch load stops');
    }

    const pickupStop = stops?.find(s => s.stop_type === 'shipper' || s.stop_type === 'pickup') || stops?.[0];
    const deliveryStop = stops?.find(s => s.stop_type === 'consignee' || s.stop_type === 'delivery') || stops?.[stops.length - 1];

    // If no stops found, use load's pickup/delivery fields as fallback
    if (!pickupStop || !deliveryStop) {
      console.warn('No load_stops found, using load pickup/delivery fields');
      const pickupLocation = load.pickup_city && load.pickup_state 
        ? `${load.pickup_city}, ${load.pickup_state}` 
        : 'Pickup location';
      const deliveryLocation = load.delivery_city && load.delivery_state
        ? `${load.delivery_city}, ${load.delivery_state}`
        : 'Delivery location';
      
      const smsMessage = `🚛 New Load Assignment!\n\nLoad: ${load.load_number}\nPickup: ${pickupLocation}\nPickup Date: ${load.pickup_date || 'TBD'}\nDelivery: ${deliveryLocation}\nDelivery Date: ${load.delivery_date || 'TBD'}\nCommodity: ${load.commodity || 'N/A'}\nWeight: ${load.weight ? `${load.weight} lbs` : 'N/A'}\n\nPlease confirm receipt.`;
      
      const formattedPhone = formatPhoneNumber(driver.phone_number);
      const smsResult = await sendSMS({
        phone: formattedPhone,
        message: smsMessage,
      });

      // Update dispatch with SMS status
      await supabase
        .from('dispatches')
        .update({
          sms_sent: smsResult.success,
          sms_sent_at: smsResult.success ? new Date().toISOString() : null,
          sms_status: smsResult.success ? 'sent' : 'failed',
          sms_message_id: smsResult.messageId || null,
          sms_error: smsResult.error || null,
        })
        .eq('id', dispatchId);

      return smsResult;
    }

    // 4. Check if thread already exists for this dispatch, or create new one
    let thread = null;
    
    // First check if dispatch already has a thread
    const { data: existingDispatch } = await supabase
      .from('dispatches')
      .select('sms_thread_id')
      .eq('id', dispatchId)
      .single();
    
    if (existingDispatch?.sms_thread_id) {
      // Thread exists, fetch it
      const { data: existingThread } = await supabase
        .from('sms_threads')
        .select('*')
        .eq('id', existingDispatch.sms_thread_id)
        .single();
      
      thread = existingThread;
      console.log('Using existing dispatch thread:', thread?.id);
    }
    
    // If no thread exists, try to find one by context_id (loadId) or create new one
    if (!thread) {
      // Check if a thread already exists for this load
      const { data: existingThreadByLoad } = await supabase
        .from('sms_threads')
        .select('*')
        .eq('context_id', loadId)
        .eq('driver_id', driverId)
        .eq('type', 'dispatch_update')
        .single();
      
      if (existingThreadByLoad) {
        thread = existingThreadByLoad;
        console.log('Found existing thread by load ID:', thread.id);
        
        // Update dispatch to link to this thread
        await supabase
          .from('dispatches')
          .update({ sms_thread_id: thread.id })
          .eq('id', dispatchId);
      } else {
        // Create new thread
        const threadSubject = `Load #${load.load_number} - ${pickupStop.city}, ${pickupStop.state} to ${deliveryStop.city}, ${deliveryStop.state}`;
        
        thread = await createThread({
          driverId: driverId,
          type: 'dispatch_update',
          subject: threadSubject,
          contextId: loadId, // Use loadId as context
          metadata: {
            dispatchId: dispatchId,
            loadNumber: load.load_number,
            truckId: truckId,
          },
        });

        if (!thread) {
          throw new Error('Failed to create SMS thread');
        }

        console.log('Created new dispatch thread:', thread.id, threadSubject);
      }
    }

    // 5. Format SMS message
    const smsMessage = formatLoadSMS({
      load_number: load.load_number,
      pickup_location: `${pickupStop.city}, ${pickupStop.state}`,
      pickup_date: pickupStop.date,
      pickup_time: pickupStop.time_from,
      delivery_location: `${deliveryStop.city}, ${deliveryStop.state}`,
      delivery_date: deliveryStop.date,
      delivery_time: deliveryStop.time_from,
      commodity: load.commodity,
      weight: load.weight,
      instructions: pickupStop.instructions || deliveryStop.instructions,
    });

    // 6. Send SMS via threaded system
    const formattedPhone = formatPhoneNumber(driver.phone_number);
    const smsSuccess = await sendThreadedSMS({
      threadId: thread.id,
      driverId: driverId,
      phoneNumber: formattedPhone,
      message: smsMessage,
    });

    const smsResult = {
      success: smsSuccess,
      messageId: null, // Will be set by threading system
      error: smsSuccess ? null : 'Failed to send threaded SMS',
    };

    // 7. Update dispatch with SMS status and thread ID
    const now = new Date().toISOString();
    const { error: updateError } = await supabase
      .from('dispatches')
      .update({
        sms_sent: smsResult.success,
        sms_sent_at: smsResult.success ? now : null,
        sms_status: smsResult.success ? 'sent' : 'failed',
        sms_message_id: smsResult.messageId || null,
        sms_error: smsResult.error || null,
        sms_thread_id: thread.id, // Link dispatch to SMS thread
      })
      .eq('id', dispatchId);

    if (updateError) {
      console.error('Failed to update dispatch SMS status:', updateError);
    }

    return {
      success: smsResult.success,
      messageId: smsResult.messageId,
      error: smsResult.error,
    };
  } catch (error) {
    console.error('Error sending dispatch SMS:', error);
    
    // Update dispatch with error
    await supabase
      .from('dispatches')
      .update({
        sms_sent: false,
        sms_status: 'failed',
        sms_error: error instanceof Error ? error.message : 'Unknown error',
      })
      .eq('id', dispatchId);

    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}

/**
 * Retry sending SMS for failed dispatch
 */
export async function retrySMS(dispatchId: string) {
  try {
    // Get dispatch details
    const { data: dispatch, error: dispatchError } = await supabase
      .from('dispatches')
      .select('load_id, driver_id, truck_id')
      .eq('id', dispatchId)
      .single();

    if (dispatchError || !dispatch) {
      throw new Error('Dispatch not found');
    }

    return await sendDispatchSMS({
      dispatchId,
      loadId: dispatch.load_id,
      driverId: dispatch.driver_id,
      truckId: dispatch.truck_id,
    });
  } catch (error) {
    console.error('Error retrying SMS:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}

/**
 * Get SMS logs for a dispatch using the threading system
 */
export async function getDispatchSMSLogs(dispatchId: string) {
  try {
    // First, get the dispatch to find its thread
    const { data: dispatch, error: dispatchError } = await supabase
      .from('dispatches')
      .select('sms_thread_id')
      .eq('id', dispatchId)
      .single();

    if (dispatchError || !dispatch || !dispatch.sms_thread_id) {
      console.log('No SMS thread found for dispatch:', dispatchId);
      return [];
    }

    // Get messages from the thread
    const { data, error } = await supabase
      .from('sms_messages')
      .select('*')
      .eq('thread_id', dispatch.sms_thread_id)
      .order('created_at', { ascending: true });

    if (error) {
      console.error('Error fetching SMS logs:', error);
      return [];
    }

    return data || [];
  } catch (error) {
    console.error('Error in getDispatchSMSLogs:', error);
    return [];
  }
}
